/**
 * Operator-facing read-only view of "my" settlements.
 * Filters server response by global.operatorCode and hides DRAFT rows.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Button, Card, Col, Divider, Empty, Modal, Row, Select, Space, Statistic, Table, Tag, Typography, message
} from "antd";
import moment from "moment";
import {
    listSettlements,
    getSettlement,
    getAdvanceBalance,
    listSettlementPayments
} from "../../actions/settlement.action";
import { isValid, isValidArray } from "../../utils/utilities";
import { SettlementInvoicePdfActions } from "../pdfs/settlement-invoice.pdf";

const { Title, Text } = Typography;
const { Option } = Select;
const ISO_DATE = "YYYY-MM-DD";

// Statuses an operator should see — only finalized settlements.
// DRAFT  = admin's pre-approval working state, not settled yet
// CANCELLED = voided; not relevant to the operator's open ledger
const VISIBLE_STATUSES = ["APPROVED", "PARTIALLY_SETTLED", "SETTLED"];
const HIDDEN_STATUSES = ["DRAFT", "CANCELLED"];

const STATUS_COLORS = {
    APPROVED: "blue",
    PARTIALLY_SETTLED: "orange",
    SETTLED: "green"
};

const CYCLE_COLORS = {
    WEEKLY: "blue",
    MONTHLY: "geekblue"
};

const MONTH_OPTIONS = [
    { num: 1,  name: "January"   }, { num: 2,  name: "February"  },
    { num: 3,  name: "March"     }, { num: 4,  name: "April"     },
    { num: 5,  name: "May"       }, { num: 6,  name: "June"      },
    { num: 7,  name: "July"      }, { num: 8,  name: "August"    },
    { num: 9,  name: "September" }, { num: 10, name: "October"   },
    { num: 11, name: "November"  }, { num: 12, name: "December"  }
];

// Plain Indian-grouped 2-decimal number (e.g. 12,34,567.89) — for quantities.
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Money: ₹ + Indian grouping, sign kept before the symbol (e.g. -₹1,234.00).
const inr = (n) => {
    const num = Number(n || 0);
    return (num < 0 ? "-₹" : "₹") + fmtNum(Math.abs(num));
};
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => d ? moment(d).format(ISO_DATE) : "—";
const fmtDateTime = (d) => d ? moment(d).format("YYYY-MM-DD HH:mm") : "—";

// Year options: last 4 years through next year (gives a 6-year window).
// Operators rarely need older — they can hit Refresh after admin posts older data.
const yearOptionsList = () => {
    const cy = new Date().getFullYear();
    const out = [];
    for (let y = cy + 1; y >= cy - 4; y--) out.push(y);
    return out;
};

const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

export const MySettlementsPage = () => {
    const operatorCode = global.operatorCode;

    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);  // 1-12
    const [statusFilter, setStatusFilter] = useState(null);

    const [loading, setLoading] = useState(false);
    const [settlements, setSettlements] = useState([]);
    const [advanceBalance, setAdvanceBalance] = useState(0);
    // Cross-period (all-time) outstanding sum for the summary card.
    // Decoupled from the table's month/year filter.
    const [allTimeOutstanding, setAllTimeOutstanding] = useState(0);

    const [modalLoading, setModalLoading] = useState(false);
    const [openSettlement, setOpenSettlement] = useState(null);
    const [payments, setPayments] = useState([]);

    const yearOptions = useMemo(yearOptionsList, []);

    // Period-overlap window for the selected calendar month.
    // listSettlements DAO already does period overlap filtering using
    // periodFrom (= os.period_end >= ?) and periodTo (= os.period_start <= ?),
    // so weekly settlements that straddle a month boundary still show up.
    const periodWindow = useMemo(() => {
        const start = `${year}-${pad2(month)}-01`;
        const end = `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`;
        return { periodFrom: start, periodTo: end };
    }, [year, month]);

    const monthLabel = useMemo(() => {
        const n = MONTH_OPTIONS.find((m) => m.num === month);
        return n ? `${n.name} ${year}` : `${year}-${pad2(month)}`;
    }, [year, month]);

    const reload = () => {
        if (!operatorCode) {
            message.error("No operator context — please re-login");
            return;
        }
        setLoading(true);
        // Defensive: operatorCode is the strict filter — even if a downstream
        // bug ever returned more, we filter again client-side.
        const filters = {
            operatorCode,
            periodFrom: periodWindow.periodFrom,
            periodTo: periodWindow.periodTo
        };
        if (statusFilter) filters.status = statusFilter;
        listSettlements(
            filters,
            (rows) => {
                const visible = (rows || []).filter((r) =>
                    r.operator_code === operatorCode &&
                    HIDDEN_STATUSES.indexOf(r.status) === -1
                );
                setSettlements(visible);
                setLoading(false);
            },
            (errMsg) => { setLoading(false); message.error(errMsg); }
        );
        getAdvanceBalance(
            { operatorCode },
            (resp) => setAdvanceBalance(resp && resp.advance_balance != null ? Number(resp.advance_balance) : 0),
            () => { /* silent */ }
        );
        // Cross-period outstanding — fetch every settlement for this operator
        // (no period filter) and sum amount_outstanding from non-hidden rows.
        listSettlements(
            { operatorCode },
            (rows) => {
                const open = (rows || []).filter((r) =>
                    r.operator_code === operatorCode &&
                    HIDDEN_STATUSES.indexOf(r.status) === -1
                );
                const total = open.reduce((sum, s) => sum + (Number(s.amount_outstanding) || 0), 0);
                setAllTimeOutstanding(total);
            },
            () => { /* silent — keep prior value */ }
        );
    };

    useEffect(() => { reload(); }, [year, month, statusFilter]);

    const openDrilldown = (id) => {
        setOpenSettlement({ loading: true });
        setModalLoading(true);
        setPayments([]);
        getSettlement(
            { id },
            (data) => {
                setModalLoading(false);
                setOpenSettlement(data);
                if (data && data.header) {
                    listSettlementPayments(
                        { settlementId: data.header.id },
                        (rows) => setPayments(rows || []),
                        () => setPayments([])
                    );
                }
            },
            (errMsg) => {
                setModalLoading(false);
                setOpenSettlement(null);
                message.error(errMsg);
            }
        );
    };

    // ---- table columns ----
    const columns = useMemo(() => [
        { title: "Settlement #", dataIndex: "settlement_no", width: 200 },
        {
            title: "Cycle",
            dataIndex: "cycle",
            width: 100,
            render: (c) => <Tag color={CYCLE_COLORS[c] || "default"}>{c}</Tag>
        },
        {
            title: "Period",
            width: 220,
            render: (_, r) => `${fmtDate(r.period_start)} → ${fmtDate(r.period_end)}`,
            sorter: (a, b) => (a.period_start || "").localeCompare(b.period_start || "")
        },
        { title: "Settlement Amt", width: 130, render: (_, r) => inr(r.invoice_amount) },
        {
            title: "Net",
            width: 130,
            render: (_, r) => {
                const n = Number(r.net_settlement);
                return n > 0
                    ? <Tag color="green">we pay {inr(n)}</Tag>
                    : n < 0
                        ? <Tag color="red">you pay {inr(-n)}</Tag>
                        : <Tag>0</Tag>;
            }
        },
        { title: "Outstanding", width: 120, render: (_, r) => inr(r.amount_outstanding) },
        {
            title: "Status",
            dataIndex: "status",
            width: 150,
            render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>
        },
        {
            title: "Action",
            width: 100,
            render: (_, r) => <Button onClick={() => openDrilldown(r.id)}>View</Button>
        }
    ], []);

    const header = openSettlement && openSettlement.header;

    return (
        <div style={{ padding: 16 }}>
            <Title level={4}>My Settlements</Title>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="Total Outstanding"
                            value={allTimeOutstanding}
                            valueStyle={{ color: allTimeOutstanding > 0 ? "#cf1322" : "#3f8600" }}
                            formatter={(v) => inr(v)}
                        />
                        <Text type="secondary">Across all open settlements (all months)</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="Advance with QwikBus"
                            value={advanceBalance}
                            valueStyle={{ color: advanceBalance > 0 ? "#3f8600" : advanceBalance < 0 ? "#cf1322" : undefined }}
                            formatter={(v) => inr(v)}
                        />
                        <Text type="secondary">
                            {advanceBalance > 0
                                ? "Available credit; auto-applied to future settlements"
                                : advanceBalance < 0 ? "Net outflow recorded" : "No advance balance"}
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="Visible Settlements"
                            value={settlements.length}
                        />
                        <Text type="secondary">{monthLabel}</Text>
                    </Card>
                </Col>
            </Row>

            <div style={{ background: "#fafafa", padding: 12, marginBottom: 12, borderRadius: 4 }}>
                <Row gutter={[12, 12]} align="middle" wrap>
                    <Col><Text strong>Year:</Text></Col>
                    <Col>
                        <Select value={year} onChange={setYear} style={{ width: 110 }}>
                            {yearOptions.map((y) => <Option key={y} value={y}>{y}</Option>)}
                        </Select>
                    </Col>
                    <Col><Text strong>Month:</Text></Col>
                    <Col>
                        <Select value={month} onChange={setMonth} style={{ width: 140 }}>
                            {MONTH_OPTIONS.map((m) => (
                                <Option key={m.num} value={m.num}>{m.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col><Text strong>Status:</Text></Col>
                    <Col>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: 180 }}
                            allowClear
                            placeholder="All visible"
                        >
                            {VISIBLE_STATUSES.map((s) => <Option key={s} value={s}>{s}</Option>)}
                        </Select>
                    </Col>
                    <Col flex="auto" />
                    <Col>
                        <Button onClick={reload} loading={loading}>Refresh</Button>
                    </Col>
                </Row>
            </div>

            <Table
                rowKey="id"
                dataSource={settlements}
                columns={columns}
                loading={loading}
                pagination={{ pageSize: 25 }}
                scroll={{ x: 1100 }}
                locale={{ emptyText: <Empty description="No settlements yet for this period" /> }}
            />

            <Modal
                visible={!!openSettlement}
                onCancel={() => setOpenSettlement(null)}
                width="min(1100px, 95vw)"
                style={{ top: 16 }}
                bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
                title={header ? `${header.settlement_no}` : "Loading…"}
                footer={[
                    <Button key="close" onClick={() => setOpenSettlement(null)}>Close</Button>
                ]}
            >
                {modalLoading && <Text>Loading…</Text>}
                {!modalLoading && header && (
                    <div>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Text type="secondary">Cycle</Text><br/>
                                <Tag color={CYCLE_COLORS[header.cycle] || "default"}>{header.cycle}</Tag>
                            </Col>
                            <Col span={12}>
                                <Text type="secondary">Status</Text><br/>
                                <Tag color={STATUS_COLORS[header.status]}>{header.status}</Tag>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: 8 }}>
                            <Col><Text type="secondary">Period: </Text><Text strong>{fmtDate(header.period_start)} → {fmtDate(header.period_end)}</Text></Col>
                        </Row>

                        <Divider orientation="left" style={{ marginTop: 24 }}>Line Items</Divider>
                        <Table
                            rowKey="id"
                            dataSource={openSettlement.lines}
                            pagination={false}
                            size="small"
                            scroll={{ x: 700 }}
                            summary={(rows) => {
                                const tQty    = rows.reduce((s, l) => s + (Number(l.quantity)      || 0), 0);
                                const tCharge = rows.reduce((s, l) => s + (Number(l.charge_amount) || 0), 0);
                                const tCredit = rows.reduce((s, l) => s + (Number(l.credit_amount) || 0), 0);
                                const gst = tCharge * 0.18;
                                const invoiceTotal = tCharge + gst;
                                return (
                                    <>
                                        <Table.Summary.Row style={{ background: "#fafafa" }}>
                                            <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}><Text strong>{fmtNum(tQty)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>—</Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(tCharge)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}><Text strong>{inr(tCredit)}</Text></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>GST (18%) on charges</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong>{inr(gst)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}><Text strong>Invoice Amount</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}><Text strong style={{ color: "#cf1322" }}>{inr(invoiceTotal)}</Text></Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </>
                                );
                            }}
                            columns={[
                                { title: "Description", dataIndex: "description" },
                                { title: "Qty", dataIndex: "quantity", width: 90, render: (v) => v == null ? "—" : fmtNum(v) },
                                { title: "Unit", dataIndex: "unit_price", width: 80, render: (v) => v == null ? "—" : v },
                                { title: "Charge", dataIndex: "charge_amount", width: 110, render: inr },
                                { title: "Credit", dataIndex: "credit_amount", width: 110, render: inr }
                            ]}
                        />

                        <div style={{ marginTop: 16, padding: 12, background: "#fafafa", borderRadius: 4 }}>
                            <Row>
                                <Col span={6}><Text>Sub Total Credits</Text></Col>
                                <Col span={6}><Text strong>{inr(header.sub_total_credits)}</Text></Col>
                                <Col span={6}><Text>Invoice Amount</Text></Col>
                                <Col span={6}><Text strong>{inr(header.invoice_amount)}</Text></Col>
                            </Row>
                            <Row>
                                <Col span={6}><Text>Opening Balance</Text></Col>
                                <Col span={6}><Text strong>{inr(header.opening_balance)}</Text></Col>
                                <Col span={6}><Text>Net Settlement</Text></Col>
                                <Col span={6}>
                                    <Text strong style={{
                                        color: Number(header.net_settlement) > 0
                                            ? "#52c41a"
                                            : Number(header.net_settlement) < 0 ? "#f5222d" : undefined
                                    }}>{inr(header.net_settlement)}</Text>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={6}><Text>Paid</Text></Col>
                                <Col span={6}><Text strong>{inr(header.amount_paid)}</Text></Col>
                                <Col span={6}><Text>Outstanding</Text></Col>
                                <Col span={6}><Text strong>{inr(header.amount_outstanding)}</Text></Col>
                            </Row>
                        </div>

                        <Divider orientation="left" style={{ marginTop: 24 }}>Settlement PDF</Divider>
                        <SettlementInvoicePdfActions header={header} lines={openSettlement.lines} />

                        <Divider orientation="left" style={{ marginTop: 24 }}>Payments</Divider>
                        {!isValidArray(payments) && <Text type="secondary">No payments recorded yet</Text>}
                        {isValidArray(payments) && (
                            <Table
                                rowKey="id"
                                size="small"
                                pagination={false}
                                dataSource={payments}
                                scroll={{ x: 700 }}
                                columns={[
                                    { title: "Date", dataIndex: "payment_date", width: 110, render: fmtDate },
                                    { title: "Direction", dataIndex: "direction", width: 110, render: (d) => <Tag color={d === "INBOUND" ? "green" : "blue"}>{d}</Tag> },
                                    { title: "Mode", dataIndex: "payment_method", width: 130 },
                                    { title: "Amount", dataIndex: "amount", width: 110, render: inr },
                                    { title: "Reference", dataIndex: "payment_reference" },
                                    { title: "Status", width: 90, render: (_, r) => r.voided_at ? <Tag color="red">VOIDED</Tag> : <Tag>active</Tag> }
                                ]}
                            />
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};
